import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import CardShell from '../components/CardShell';
import { getBsView, setBsLineForecast } from '../api/financials_bs';
import { getPlView } from '../api/financials_pl';
import { getForecastAssumptions, putForecastAssumption } from '../api/forecastAssumptions';
import { ROLLFORWARD, DEFAULT_PROFILE, defaultDelay, computeRollforward } from './financials/forecastRollforward';
import './ForecastBalanceSheet.css';

/* Bilan prévisionnel — vue + édition (P1 du chantier « prévisionnel intégré »).
   Réutilise compute_bs_view (getBsView flat_forecast) : bilan hybride réel
   (mois ≤ clôture) / prévisionnel reporté à plat (mois > clôture), la Liquidité
   plug équilibre le bilan. Sections repliables, départ + 12 mois.
   Édition inline des mois prévisionnels (sauf lignes dérivées : Liquidité = plug,
   Résultat = cumul P&L) → persistée dans bs_line_forecast, le cash se recale.
   Drawer + drivers par ligne = P2. */

const fmt = (v, unit) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Math.round(Number(v) / (unit === 'chf' ? 1 : 1000));
  return n === 0 ? '–' : n.toLocaleString('de-CH');
};
// Texte d'audit d'une cellule prévisionnelle (survol) : d'où vient le chiffre.
function auditText(src, value, unit, p) {
  if (!src) return undefined;
  const money = (x) => fmt(x, unit) + (unit === 'chf' ? ' CHF' : ' kCHF');
  switch (src.src) {
    case 'real': return p('Réel (mois clôturé)');
    case 'manual': return `${p('Saisi manuellement')} = ${money(value)}`;
    case 'flat': return `${p('Report à plat (dernier solde connu)')} = ${money(value)}`;
    case 'pl': return p('Résultat cumulé du compte de résultat');
    case 'driver': {
      if (src.ratio_key === 'stock_rollforward') return `${p('Roulement : stock du mois précédent + variation de stock (P&L)')} ${money(src.base)} = ${money(value)}`;
      if (src.ratio_key === 'pac') return `${p('Acomptes')} = ${src.ratio}% ${p('du')} ${src.flow_label} ${p('annuel')} ${money(src.base)} = ${money(value)}`;
      const vatPart = src.vat ? ` × ${(1 + src.vat).toFixed(3)} (${p('TVA')})` : '';
      const openPart = src.opening ? ` + ${money(src.opening)} ${p('ouverture')}` : '';
      const stuckPart = src.stuck ? ` + ${money(src.stuck)} ${p('bloqué')}` : '';
      // Connecté au plan des ventes : la créance suit la facturation et les
      // encaissements TTC du plan, sans délai.
      if (src.terms_plan) {
        return `${p('Plan des ventes')} : ${p('facturé')} ${money(src.terms_plan.billing)} − ${p('encaissé')} ${money(src.terms_plan.cash)}${openPart}${stuckPart} = ${money(value)}`;
      }
      return `${p('Driver')} ${String(src.ratio_key || '').toUpperCase()} : ${src.flow_label} ${p('des')} ${src.ratio} ${p('derniers jours')} ${money(src.base)}${vatPart}${openPart}${stuckPart} = ${money(value)}`;
    }
    default: return undefined;
  }
}

// Explication compacte d'une cellule (liste du drawer).

// Zéro est une VALEUR (solde forcé à zéro), pas une cellule vide : seuls null /
// undefined / non-numérique rendent la chaîne vide. Sans ça un 0 saisi s'affiche
// blanc, et le blur suivant l'interprète comme « vidée » → efface la prévision.
const toUnit = (v, unit) => (
  v == null || v === '' || !Number.isFinite(Number(v))
    ? ''
    : Math.round(Number(v) / (unit === 'chf' ? 1 : 1000))
);
const fromUnit = (s, unit) => {
  const p = parseFloat(String(s).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(p) ? p * (unit === 'chf' ? 1 : 1000) : 0;
};
// Saisie stricte : NaN si la chaîne n'est pas un nombre (≠ fromUnit qui renvoie 0).
const parseInput = (s, unit) => {
  const p = parseFloat(String(s).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(p) ? p * (unit === 'chf' ? 1 : 1000) : NaN;
};

function useBsView(tenantId, year) {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  // Garde d'ordre : chaque saisie déclenche un rechargement, et deux requêtes en
  // vol peuvent revenir dans le désordre — une réponse antérieure à la dernière
  // écriture réafficherait l'ancienne valeur. Seule la plus récente est retenue.
  const reqSeq = useRef(0);
  const load = useCallback(() => {
    if (!tenantId || !year) { setView(null); return; }
    const seq = ++reqSeq.current;
    setLoading(true);
    getBsView(tenantId, year, { flatForecast: true })
      .then((r) => {
        if (seq !== reqSeq.current) return;          // réponse périmée → ignorée
        setView(r.error ? null : r.data); setLoading(false);
      })
      .catch(() => {
        if (seq !== reqSeq.current) return;
        setView(null); setLoading(false);
      });
  }, [tenantId, year]);
  useEffect(() => { load(); }, [load]);
  return { view, loading, reload: load };
}

/* Un montant saisi (solde initial, 2026-09-28) : il s'écrit à la sortie du
   champ, pas à chaque frappe, qui relancerait le calcul du bilan. */
function AmountIn({ chf, unit, onCommit, className = '', base = 'fbs-src-in' }) {
  const [draft, setDraft] = useState(null);
  const shown = draft != null ? draft : (chf == null ? '' : String(toUnit(chf, unit)));
  const commit = () => {
    if (draft == null) return;
    const v = String(draft).trim() === '' ? null : parseInput(draft, unit);
    setDraft(null);
    if (v === null || Number.isFinite(v)) onCommit(v);
  };
  return (
    <input className={`${base} ${className}`} inputMode="decimal" placeholder="—" value={shown}
      onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  );
}

// Origine d'une variation prévisionnelle (popover « œil » du bilan complet).
function varOriginLabel(src, p, isRoll = false) {
  if (src && Array.isArray(src.moves) && src.moves.length) {
    return src.moves.map((mv) => `${p('Mouvement exceptionnel')} « ${mv.label || '—'} »`).join(' · ');
  }
  if (isRoll) return p('Roulement (détail en dépliant la ligne)');
  if (!src) return p('Report à plat (dernier solde connu)');
  switch (src.src) {
    case 'manual': return p('Saisie manuelle (ligne du bilan)');
    case 'driver': return src.ratio_key === 'stock_rollforward'
      ? p('Roulement du stock')
      : `${p('Driver')} ${String(src.ratio_key || '').toUpperCase()}`;
    case 'role': return src.role === 'vat' ? p('Décompte TVA') : p('Charges sociales — échéancier');
    case 'pl': return p('Résultat du compte de résultat');
    case 'plug': return p('Liquidité — équilibre du bilan (cash-plug)');
    case 'flat': return p('Report à plat (dernier solde connu)');
    case 'zero': return '—';
    default: return src.src || '—';
  }
}

// Cellule mensuelle : lecture, ou saisie (mois prévisionnel d'une ligne éditable).
function MonthCell({ value, unit, isFc, first, editable, onCommit, title, varMark = false, varTip = null }) {
  const [draft, setDraft] = useState(null);
  const [initial, setInitial] = useState(null);   // texte à la prise de focus
  const cls = `fbs-num${Number(value) < 0 ? ' is-neg' : ''}${isFc ? ' is-fc' : ''}${first ? ' is-fc-edge' : ''}${varMark ? ' is-var' : ''}${varTip ? ' fbs-tipcell' : ''}`;
  if (!(editable && isFc)) {
    return (
      <td className={cls} title={varTip ? undefined : title}>
        {fmt(value, unit)}
        {varTip && <span className="fbs-vartip">{varTip}</span>}
      </td>
    );
  }
  return (
    <td className={`${cls} fbs-editcell`} title={title}>
      <input
        className="fbs-edit"
        inputMode="decimal"
        value={draft !== null ? draft : toUnit(value, unit)}
        onFocus={() => { const t = String(toUnit(value, unit)); setInitial(t); setDraft(t); }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          // On n'écrit QUE si le texte a changé depuis la prise de focus. Comparer
          // à `value` ne suffit pas : l'affichage est arrondi au millier (kCHF) et
          // le brouillon peut être périmé (rechargement pendant la saisie) — un
          // simple passage de curseur écrivait alors une valeur non voulue.
          const changed = draft !== null && draft !== initial;
          if (changed) {
            const trimmed = String(draft).trim();
            if (trimmed === '') {
              onCommit(null);                 // cellule vidée → retour à l'auto (driver / report à plat)
            } else {
              const amt = parseInput(trimmed, unit);   // nombre (0 inclus) → valeur explicite
              if (Number.isFinite(amt)) onCommit(amt);
            }
          }
          setDraft(null); setInitial(null);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      />
    </td>
  );
}

// Roulement créances/dettes : moteur partagé dans ./financials/forecastRollforward.

// Filtre de l'en-tête (pastilles) : par section du bilan.
const ACTIF_SECS = new Set(['current_assets', 'fixed_assets']);
const sectionSide = (code) => (ACTIF_SECS.has(code) ? 'actif' : 'passif');
const SUBTOTAL_SIDE = { total_assets: 'actif', total_liabilities_equity: 'passif' };   // balance_check = les deux
/* Focalisation par ÉTAPE du parcours (2026-08-09) — le tableau du bilan
   prévisionnel n'est pas réécrit : il est FILTRÉ. Le calcul, les profils
   d'écoulement, les lignes de planification et l'audit par cellule restent
   exactement ce qu'ils étaient ; seule change la portion montrée à l'écran.
   C'est délibéré : ce moteur est le plus fiable du prévisionnel, le refaire pour
   le découper aurait risqué de l'abîmer.

   `keys` limite aux clés standard citées · `exclude` retire les postes traités
   par une autre étape · `plans` choisit les lignes de planification affichées. */
export const BS_FOCUS = {
  'fc-invest':    { sections: ['fixed_assets'], plans: ['cessions', 'capex'] },
  'fc-stock':     { sections: ['current_assets'], keys: ['inventory'] },
  // ── Positions mono-poste (2026-08-11) : seuls les postes à MÉCANIQUE propre
  // (roulement, délais) ont leur section. Les autres postes circulants
  // (acomptes, dettes fiscales, TVA, comptes courants…) se saisissent
  // directement au bilan. La liquidité reste le plug, lue à l'étape Σ.
  'pos-clients':  { sections: ['current_assets'], keys: ['ar_clients'] },
  'pos-fourn':    { sections: ['current_liabilities'], keys: ['ap_suppliers'] },
  // Acomptes clients (2026-09-13) : le solde d'ouverture se reprend en CA sur n
  // mois, les nouveaux viennent du plan des ventes ou des règles par ligne.
  'pos-advances': { sections: ['current_liabilities'], keys: ['customer_advances'] },
  // Stocks (Fonds de roulement, 2026-09-30) : le stock roule sur la variation
  // du compte de résultat ; il se lit, il ne se tape pas.
  'pos-stock':    { sections: ['current_assets'], keys: ['inventory'] },
  /* FINANCEMENT = LA DETTE, et elle seule (2026-09-18). Les fonds propres y
     figuraient depuis qu'un apport en capital finance l'entreprise autant qu'un
     emprunt ; mais leur carte ne se saisissait pas, elle se lisait — le capital,
     les réserves et le résultat viennent d'ailleurs. Une augmentation de capital
     s'écrit dans « Mouvements exceptionnels », qui est fait pour les écritures
     qui ne se répètent pas. */
  /* PLUS DE LIGNES DE PLAN ICI (2026-09-24) : elles venaient de l'hypothèse
     `bs_financing`, que plus rien ne lit depuis que les emprunts vivent dans
     `fin_loans`. Les afficher faisait voisiner deux financements dont un mort. */
  'fc-financing': { sections: ['long_term_liabilities'] },
  // Σ — bilan entier, en lecture seule : c'est un résultat, pas une saisie.
  'fc-balance':   { readOnly: true },
};

const SECTION_PILLS = [
  ['current_assets', 'Actif circulant'],
  ['fixed_assets', 'Actif immobilisé'],
  ['current_liabilities', 'Dettes à court terme'],
  ['long_term_liabilities', 'Dettes à long terme'],
  ['equity', 'Fonds propres'],
];

// Chevron SVG (repris du compte de résultat) : pivote à l'ouverture ; l'espace
// (slot 16px) est toujours réservé, la flèche n'apparaît qu'au survol ou si ouverte
// → libellés alignés, lecture épurée.
const Chevron = ({ open }) => (
  <svg className={`fbs-chev${open ? ' is-open' : ''}`} viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
    <polyline points="9 6 15 12 9 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function ForecastBalanceSheetTab({ activeTenant, year, unit = 'kchf', onAssumptionsChanged, focus = null, closeAction = null }) {
  const { t } = useTranslation();
  const p = useP();
  const monthLabels = useMemo(
    () => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)),
    [t],
  );
  const tid = activeTenant?.id;
  const { view, loading, reload } = useBsView(tid, year);
  const [side, setSide] = useState('all');   // filtre en-tête : all | code de section
  // Portée de l'étape en cours (null = bilan entier, hub Prévisionnel).
  const fc = (focus && BS_FOCUS[focus]) || null;
  const roView = !!fc?.readOnly;
  // Bilan COMPLET (hub Prévisionnel, aucune étape) : LECTURE SEULE depuis le
  // 2026-08-20 — tout se saisit ailleurs (sections, moteurs, mouvements
  // exceptionnels), le bilan lit. Les variations des mois prévisionnels sont
  // mises en évidence, l'œil en bout de ligne liste leur origine.
  const fullView = !fc;
  const pFmt = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  // Étape mono-poste (créances, fournisseurs, acomptes…) : vue FIGÉE — pas de
  // ligne de section, pas d'agrégation, le roulement toujours déplié. La page
  // ne parle que d'un poste ; replier reviendrait à la vider.
  const fixedView = !!(fc && !fc.readOnly && fc.keys);
  // Dans le bilan ENTIER (aucune étape active), les postes qui ONT leur étape
  // ne s'éditent plus ici : un poste se travaille à un seul endroit, le bilan
  // le lit (décision Olivier, 2026-08-10). Ils restent visibles.
  const STEP_OWNED = new Set(['ar_clients', 'ap_suppliers', 'inventory']);
  // Sections entières possédées par une étape : immobilisations (Capex &
  // Amort.), financement (dettes LT + fonds propres).
  const STEP_OWNED_SECTIONS = new Set(['fixed_assets', 'long_term_liabilities', 'equity']);
  const lockedInBilan = (code) => !fc
    && (STEP_OWNED.has(meta[code]?.std_key) || STEP_OWNED_SECTIONS.has(meta[code]?.section));
  // Les lignes de planification (capex, cessions, financement) appartiennent
  // aussi à leurs étapes : dans le bilan entier, lecture seule.
  const planRo = roView || !fc;
  const inFocus = (r) => {
    if (!fc || fc.readOnly) return true;
    if (fc.sections && !fc.sections.includes(r.section)) return false;
    const k = meta[r.code]?.std_key || null;
    if (fc.keys) return fc.keys.includes(k);
    if (fc.exclude) return !fc.exclude.includes(k);
    return true;
  };
  // Les lignes de PLANIFICATION (capex, cessions, financement) n'apparaissent
  // que dans leur section (et en lecture au Σ Bilan) : dans le bilan entier,
  // seuls leurs agrégats calculés (Nouveaux actifs, Cessions, Financement)
  // restent visibles — on ne planifie plus directement dans le bilan.
  const planShown = (name) => !!fc && (fc.readOnly || (fc.plans || []).includes(name));
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggle = (code) => setCollapsed((prev) => {
    const n = new Set(prev);
    if (n.has(code)) n.delete(code); else n.add(code);
    return n;
  });

  const closed = view?.closed_count || 0;
  const rows = useMemo(() => view?.rows || [], [view]);
  const sections = view?.sections || [];
  const subtotals = rows.filter((r) => r.type === 'subtotal');
  // Agréger / déployer tous les groupes (sections). En déployant, on s'arrête au
  // niveau ligne : les sous-détails du roulement (encaissements) restent repliés.
  const secCodes = sections.map((s) => s.code);
  const allSecCollapsed = secCodes.length > 0 && secCodes.every((c) => collapsed.has(c));
  const rollLineIds = (view?.lines || []).filter((l) => ROLLFORWARD[l.std_key]).map((l) => l.id);
  const toggleAllGroups = () => {
    if (allSecCollapsed) { setCollapsed(new Set()); setExpandedRoll(new Set()); }
    else { setCollapsed(new Set(secCodes)); }
  };
  // Métadonnées par ligne (id → std_key / is_pl_feed) pour l'éditabilité.
  const meta = useMemo(() => Object.fromEntries((view?.lines || []).map((l) => [l.id, l])), [view]);

  // ── Roulement de créances (profils et échéances PERSISTÉS depuis le 2026-08-09) ──
  // P&L : revenus mensuels (HT) → source des « nouvelles ventes » (brutifiées TTC).
  const [plFlows, setPlFlows] = useState(null);   // { revenue:[12], purch:[12] } HT
  useEffect(() => {
    if (!tid || !year) { setPlFlows(null); return; }
    getPlView(tid, year).then((r) => {
      if (r.error) { setPlFlows(null); return; }
      const rows2 = r.data?.rows || [];
      const sec = (code) => { const x = rows2.find((y) => y.code === code); return Array.from({ length: 12 }, (_, i) => Number(x?.months?.[String(i + 1)]) || 0); };
      // Seules les CHARGES DIRECTES alimentent les dettes fournisseurs : les
      // autres charges d'exploitation sont payées le mois même (pas de dette).
      const cogs = sec('sec:cogs');
      setPlFlows({ revenue: sec('sec:revenue'), purch: cogs.map((c) => Math.abs(c)) });
    }).catch(() => setPlFlows(null));
  }, [tid, year]);
  const [expandedRoll, setExpandedRoll] = useState(() => new Set());   // replié par défaut → set = lignes dépliées
  // Détail des nouvelles ventes et de leur encaissement, déplié au clic (2026-09-28).
  const [arOpen, setArOpen] = useState({ new: false, enc: false });
  const toggleRoll = (id) => setExpandedRoll((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  // Échéances négociées et profils d'écoulement : édités en local pour la
  // fluidité de frappe, PERSISTÉS côté serveur (clés `bs_arrangements` et
  // `bs_roll_profiles` des hypothèses). Ils vivaient en mémoire du navigateur
  // et disparaissaient à chaque rechargement de page.
  // Arrangements par ligne : LISTE nommée [{ id, label, vals: {mois: montant} }]
  // (« ajouter des arrangements supplémentaires », 2026-08-10). L'ancien format
  // — un seul dict par ligne — est adopté comme premier arrangement sans nom.
  const [arrangements, setArrangements] = useState({});
  const arrList = (lineId) => {
    const a = arrangements[lineId];
    if (Array.isArray(a)) return a;
    if (a && typeof a === 'object' && Object.keys(a).length) return [{ id: 'a0', label: '', vals: a }];
    return [];
  };
  // Le moteur de roulement consomme la SOMME : plusieurs plans de paiement, un
  // seul échéancier effectif.
  const arrSum = (lineId) => {
    const out = {};
    arrList(lineId).forEach((a) => Object.entries(a.vals || {}).forEach(([m, v]) => {
      const n = Number(v); if (Number.isFinite(n) && n !== 0) out[m] = (out[m] || 0) + n;
    }));
    return out;
  };
  const addArr = (lineId) => setArrangements((prev) => ({
    ...prev, [lineId]: [...arrList(lineId), { id: `a${Date.now()}`, label: '', vals: {} }],
  }));
  const delArr = (lineId, arrId) => setArrangements((prev) => (
    { ...prev, [lineId]: arrList(lineId).filter((a) => a.id !== arrId) }
  ));
  const labelArr = (lineId, arrId, label) => setArrangements((prev) => ({
    ...prev, [lineId]: arrList(lineId).map((a) => (a.id === arrId ? { ...a, label } : a)),
  }));
  const [profiles, setProfiles] = useState({});           // { lineId: { open:[p1,p2], dso:jours } }
  const [editProfile, setEditProfile] = useState(null);   // `${lineId}:${kind}` du réglage en cours
  const [popPos, setPopPos] = useState({ x: 0, y: 0 });   // position mesurée du popup de réglage
  const setArr = (lineId, arrId, month, amount) => setArrangements((prev) => ({
    ...prev,
    [lineId]: arrList(lineId).map((a) => (a.id === arrId
      ? { ...a, vals: { ...(a.vals || {}), [month]: amount } } : a)),
  }));
  const getProfile = (lineId) => profiles[lineId]?.open || DEFAULT_PROFILE;   // postes ouverts : profil %
  const setProfile = (lineId, idx, pct) => setProfiles((prev) => {
    const line = { ...(prev[lineId] || {}) };
    const cur = (line.open || DEFAULT_PROFILE).slice();
    cur[idx] = pct;
    line.open = cur;
    const next = { ...prev, [lineId]: line };
    persistProfiles(next);
    return next;
  });
  // Délai par défaut propre à la ligne : 45 j pour les créances (DSO), 30 j pour
  // les dettes fournisseurs (DPO = achats du mois réglés le mois suivant).
  const getDso = (lineId) => (profiles[lineId]?.dso != null ? profiles[lineId].dso : defaultDelay(meta[lineId]?.std_key));
  const setDso = (lineId, days) => setProfiles((prev) => {
    const next = { ...prev, [lineId]: { ...(prev[lineId] || {}), dso: days } };
    persistProfiles(next);
    return next;
  });

  const saveCell = useCallback((lineId, monthIdx, amount) => {
    if (!tid) return;
    setBsLineForecast(tid, { year, reporting_line_id: lineId, month: monthIdx + 1, amount })
      .then((r) => { if (!r.error) reload(); });
  }, [tid, year, reload]);
  // Purge des saisies manuelles d'une ligne (y compris les 0 invisibles) :
  // amount null = effacement serveur, la ligne revient au calcul.
  const onPurgeManual = useCallback(async (lineId, months) => {
    if (!tid || !months.length) return;
    await Promise.all(months.map((m) => setBsLineForecast(tid, { year, reporting_line_id: lineId, month: m, amount: null })));
    reload();
  }, [tid, year, reload]);

  // Hypothèses (ratios DSO/DPO/DIO + règles de comportement par ligne).
  const [assum, setAssum] = useState({});
  const loadAssum = useCallback(() => {
    if (!tid || !year) return;
    getForecastAssumptions(tid, year).then((r) => { if (!r.error) setAssum(r.data || {}); });
  }, [tid, year]);
  useEffect(() => { loadAssum(); }, [loadAssum]);
  const saveRatio = useCallback((key, value) => {
    setAssum((a) => ({ ...a, [key]: value }));          // optimiste
    putForecastAssumption(tid, year, key, value).then(() => { reload(); onAssumptionsChanged && onAssumptionsChanged(); });
  }, [tid, year, reload, onAssumptionsChanged]);

  // Hydratation : on recharge les réglages sauvegardés au changement de client
  // ou d'année seulement — pas à chaque frappe, qui écraserait la saisie en cours.
  const hydrated = useRef(null);
  useEffect(() => {
    const k = `${tid}:${year}`;
    if (!tid || !year || hydrated.current === k || !Object.keys(assum).length) return;
    hydrated.current = k;
    setArrangements(assum.bs_arrangements || {});
    setProfiles(assum.bs_roll_profiles || {});
  }, [tid, year, assum]);

  // Écriture : on purge les cellules vides pour ne pas laisser de résidu en base.
  const persistProfiles = useCallback((next) => saveRatio('bs_roll_profiles', next), [saveRatio]);
  const persistArrangements = useCallback(() => {
    const clean = {};
    Object.entries(arrangements).forEach(([lineId, a]) => {
      const list = (Array.isArray(a) ? a : [{ id: 'a0', label: '', vals: a || {} }])
        .map((x) => ({
          ...x,
          vals: Object.fromEntries(Object.entries(x.vals || {})
            .filter(([, v]) => v !== '' && v != null && Number(v) !== 0)),
        }))
        .filter((x) => Object.keys(x.vals).length || (x.label || '').trim());
      if (list.length) clean[lineId] = list;
    });
    saveRatio('bs_arrangements', clean);
  }, [arrangements, saveRatio]);

  // Lignes de planification éditées AU BILAN (source unique) : nouvel actif
  // (bs_capex, avec taux d'amort annuel) et financement (bs_financing). Le backend
  // les applique aux immo / dettes LT + amort au compte de résultat.
  const capexLines = useMemo(() => (Array.isArray(assum.bs_capex) ? assum.bs_capex : []), [assum]);
  const finLines = useMemo(() => (Array.isArray(assum.bs_financing) ? assum.bs_financing : []), [assum]);
  const cessionLines = useMemo(() => (Array.isArray(assum.bs_cessions) ? assum.bs_cessions : []), [assum]);
  const planOps = (key, lines) => ({
    add: () => saveRatio(key, [...lines, { id: `p${Date.now()}`, label: '', type: 'in', rate: 0, vals: {} }]),
    del: (id) => saveRatio(key, lines.filter((l) => l.id !== id)),
    label: (id, label) => saveRatio(key, lines.map((l) => (l.id === id ? { ...l, label } : l))),
    rate: (id, rate) => saveRatio(key, lines.map((l) => (l.id === id ? { ...l, rate: Number(rate) || 0 } : l))),
    toggle: (id) => saveRatio(key, lines.map((l) => {
      if (l.id !== id) return l;
      const type = l.type === 'out' ? 'in' : 'out';
      const vals = {}; Object.entries(l.vals || {}).forEach(([k, v]) => { vals[k] = (type === 'out' ? -Math.abs(v) : Math.abs(v)); });
      return { ...l, type, vals };
    })),
    setVal: (id, m, raw) => saveRatio(key, lines.map((l) => {
      if (l.id !== id) return l;
      const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
      const vals = { ...(l.vals || {}) };
      if (!Number.isFinite(n)) delete vals[String(m)];
      else vals[String(m)] = (l.type === 'out' ? -Math.abs(n) : Math.abs(n)) * (unit === 'chf' ? 1 : 1000);
      return { ...l, vals };
    })),
  });

  // Plan des charges sociales (même moteur que l'onglet Charges sociales) → pilote
  // la ligne « Assurances sociales » (social_debt) du bilan. Mouvement mensuel de
  // dette = accrual (charges Empl+Empr) − décaissement (assurances = masse annoncée).
  /* La série des charges sociales est désormais CALCULÉE PAR LE SERVEUR au
     moment où le bilan se construit (moteur migré du JavaScript le 2026-08-21) :
     plus rien à publier depuis cet écran, la ligne à rôle « Assurances
     sociales » est toujours à jour quel que soit l'ordre de visite des vues. */

  // Roulement par ligne + override d'affichage (créances = roll-forward ; la
  // Liquidité absorbe l'écart vs backend → le bilan reste équilibré, même section).
  const { rollByLine, override } = useMemo(() => {
    const rl = {}; const ov = {};
    if (!view || !plFlows) return { rollByLine: rl, override: ov };
    const liq = (view.lines || []).find((l) => l.std_key === 'liquidity');
    const liqDelta = {};
    for (const l of (view.lines || [])) {
      const cfg = ROLLFORWARD[l.std_key];
      if (!cfg) continue;
      const dr = rows.find((r) => r.type === 'detail' && r.code === l.id);
      if (!dr) continue;
      // Flux TTC du SERVEUR : la TVA est appliquée par ligne (drapeau « sans
      // TVA » des Paramètres respecté) — plus de grossissement global ici.
      const ft = view.flows_ttc || {};
      // Fournisseurs = ACHATS seuls (les charges d'exploitation se paient le
      // mois même, décision 2026-08-17) — même base que le serveur.
      const flowTTC = cfg.flow === 'purch'
        ? (ft.purchases || []).map(Number)
        : (ft.revenue || []).map(Number);
      // Ouverture : solde au dernier mois clos — ou déc. N-1 (prévisionnel
      // enchaîné) quand l'année n'a AUCUN mois clos (ex. N+1) ; partir de 0
      // écrasait l'ouverture correcte du serveur.
      let opening = closed >= 1
        ? Number(dr.months?.[String(closed)] || 0)
        : (Number(dr.prev_dec) || 0);
      // Point de départ AUTONOME (2026-09-28) : le solde tapé ouvre le
      // roulement, comme au serveur.
      const oiv = view.cash_plan?.opening_items;
      if (oiv?.editable?.[cfg.flow === 'purch' ? 'ap' : 'ar']) {
        const typedOpen = cfg.flow === 'purch' ? oiv.rows?.suppliers_open : oiv.rows?.clients_open;
        opening = typedOpen == null ? 0 : (cfg.flow === 'purch' ? -Number(typedOpen) : Number(typedOpen));
      }
      // Connecté au module Ventes (2026-09-12) : la facturation et les
      // encaissements TTC du plan viennent du SERVEUR (view.ar_terms.plan) ;
      // l'écran ne les recalcule pas, et le DSO ne s'applique plus aux
      // nouvelles ventes.
      const terms = (cfg.flow === 'revenue' && view.ar_terms && view.ar_terms.plan) ? view.ar_terms.plan : null;
      const buckets = terms ? [{ days: 0, flow: terms.billing, collect: terms.cash }] : null;
      const roll = computeRollforward({ opening, closed, flowTTC, arrangements: arrSum(l.id), profileOpen: getProfile(l.id), dsoNew: getDso(l.id), buckets });
      rl[l.id] = roll;
      ov[l.id] = {};
      for (let m = closed + 1; m <= 12; m++) {
        const backend = Number(dr.months?.[String(m)] || 0);
        ov[l.id][m] = roll[m].balance;
        // Actif (+1) : la liquidité absorbe à l'inverse (même section). Passif (−1) :
        // une dette plus haute = moins de cash sorti → la liquidité monte.
        liqDelta[m] = (liqDelta[m] || 0) + cfg.sign * (roll[m].balance - backend);
      }
    }
    /* La dette sociale n'est PLUS surchargée ici (2026-08-18) : le serveur
       l'écrit sur la ligne portant le rôle « Assurances sociales », depuis la
       série publiée par la vue Charges sociales. Une surcharge d'écran créait
       une clôture qui n'existait que dans le navigateur — d'où l'écart entre le
       solde final d'une année et l'ouverture de la suivante, qui reprend, elle,
       la valeur du serveur. */
    if (liq && Object.keys(liqDelta).length) {
      const liqRow = rows.find((r) => r.code === liq.id);
      ov[liq.id] = {};
      Object.keys(liqDelta).forEach((m) => { ov[liq.id][m] = Number(liqRow?.months?.[String(m)] || 0) - liqDelta[m]; });
    }
    return { rollByLine: rl, override: ov };
  }, [view, plFlows, rows, closed, arrangements, profiles, assum]);

  if (loading && !view) return <p className="fps-loading">{t('common:status.loading')}</p>;
  if (!view) return <p className="fps-loading">{p('Aucun bilan pour cette année.')}</p>;

  // Ligne dérivée (non éditable) : Liquidité (plug) ou Résultat de l'exercice (cumul P&L).
  const isDerived = (lineId) => {
    const l = meta[lineId];
    // Stock : dérivé du roulement (variation de stock du compte de résultat) → non
    // éditable au bilan, valeurs en noir (le bilan s'ajuste automatiquement).
    return !l || l.std_key === 'liquidity' || l.std_key === 'inventory' || l.is_pl_feed;
  };

  /* LE SOLDE INITIAL SE TAPE SUR LE DERNIER MOIS RÉEL (demande d'Olivier,
     2026-09-28) : en source manuelle, la cellule de clôture des créances
     clients et des dettes fournisseurs se saisit, fond jaune ; en source
     Finance, elle porte le solde des comptes, comme tout mois réel. Sans mois
     clos, c'est la colonne d'ouverture qui se tape. */
  const initFor = (code) => {
    const oiv = view?.cash_plan?.opening_items;
    const k = meta[code]?.std_key;
    if (!fixedView || (k !== 'ar_clients' && k !== 'ap_suppliers')) return null;
    const isAr = k === 'ar_clients';
    if (!oiv?.editable?.[isAr ? 'ar' : 'ap']) return null;
    const raw = oiv.rows?.[isAr ? 'clients_open' : 'suppliers_open'];
    return {
      value: raw == null ? null : (isAr ? Number(raw) : -Number(raw)),
      save: (chf) => {
        const cur = (assum.opening_items && typeof assum.opening_items === 'object') ? assum.opening_items : {};
        const ak = isAr ? 'ar' : 'ap';
        const it = { ...(cur[ak] || {}) };
        if (chf == null) delete it.amount; else it.amount = Math.abs(chf);
        saveRatio('opening_items', { ...cur, [ak]: it });
      },
    };
  };
  const initTd = (ini, key) => (
    <td key={key} className="fbs-num fbs-editcell fbs-initcell" title={p('Solde initial, à saisir')}>
      <AmountIn chf={ini.value} unit={unit} onCommit={ini.save} base="fbs-edit" />
    </td>
  );
  const openCell = (r) => {
    const ini = r?.code && closed < 1 ? initFor(r.code) : null;
    if (ini) return initTd(ini, 'open');
    return <td className={`fbs-num fbs-open${Number(r?.prev_dec) < 0 ? ' is-neg' : ''}`}>{fmt(r?.prev_dec, unit)}</td>;
  };
  const readMonths = (r) => monthLabels.map((_, i) => {
    const v = r?.months ? r.months[String(i + 1)] : null;
    return <td key={i} className={`fbs-num${Number(v) < 0 ? ' is-neg' : ''}${i + 1 > closed ? ' is-fc' : ''}${i + 1 === closed + 1 ? ' is-fc-edge' : ''}`}>{fmt(v, unit)}</td>;
  });

  // Lignes de planification éditables (nouvel actif / financement) — saisie jaune,
  // boutons +/− (charte), commit au blur. kind='capex' ajoute un champ taux d'amort.
  const div = unit === 'chf' ? 1 : 1000;
  /* `roView` (étape Σ Bilan) : les lignes restent visibles — elles font partie
     du bilan — mais aucune commande d'édition n'est rendue. Le bilan est un
     RÉSULTAT : on ne le corrige pas, on corrige l'étape qui le produit. */
  /* L'ENTRÉE OUVRE LE BLOC (2026-09-18) : « + Financement » fermait la liste,
     sous la dernière ligne, là où l'œil ne va qu'après avoir tout lu. On y
     arrive pourtant pour AJOUTER. Elle se nomme en entier, et se voit. */
  const renderPlanRows = (lines, ops, kind) => (
    <>
      {!planRo && (
        <tr className="fbs-addrow fbs-addrow--top">
          <td className="fbs-lbl" colSpan={14}>
            <button type="button" className="fbs-addbtn fbs-addbtn--strong" onClick={ops.add}>
              {kind === 'capex' ? p('Ajouter un actif')
                : kind === 'cessions' ? p('Ajouter une cession')
                : p('Ajouter un financement')}
            </button>
          </td>
        </tr>
      )}
      {lines.map((ln) => (
        <tr key={ln.id} className="fbs-detail fbs-planline">
          <td className="fbs-lbl">
            <span className="fbs-plan-head">
              {kind === 'cessions' ? (
                <span className="fbs-plan-type fbs-plan-type--out" title={p("Cession (sortie d'actif → cash)")}>−</span>
              ) : (
                <button type="button" className={`fbs-plan-type fbs-plan-type--${ln.type === 'out' ? 'out' : 'in'}`}
                  disabled={planRo}
                  title={ln.type === 'out' ? p('Diminution (−)') : p('Ajout (+)')} onClick={() => (planRo ? null : ops.toggle(ln.id))}>{ln.type === 'out' ? '−' : '+'}</button>
              )}
              <input className="fbs-plan-lbl" placeholder={kind === 'capex' ? p('Nouvel actif…') : (kind === 'cessions' ? p('Cession…') : p('Financement…'))}
                disabled={planRo}
                defaultValue={ln.label || ''} onBlur={(e) => e.target.value !== (ln.label || '') && ops.label(ln.id, e.target.value)} />
              {(kind === 'capex' || kind === 'financing') && (
                <span className="fbs-plan-rate" title={kind === 'capex' ? p("Taux d'amortissement annuel (%)") : p("Taux d'intérêt annuel (%)")}>
                  <input type="text" inputMode="decimal" disabled={planRo} defaultValue={ln.rate ?? ''} onBlur={(e) => Number(e.target.value) !== (ln.rate || 0) && ops.rate(ln.id, e.target.value)} />%
                </span>
              )}
              {!planRo && (
              <button type="button" className="fbs-plan-del" title={p('Supprimer')} onClick={() => ops.del(ln.id)}>×</button>
              )}
            </span>
          </td>
          <td className="fbs-num fbs-open" />
          {monthLabels.map((_, i) => {
            const m = i + 1;
            const raw = ln.vals?.[String(m)];
            const disp = raw == null ? '' : Math.round(Math.abs(raw) / div);
            return (
              <td key={i} className={`fbs-num${i + 1 > closed ? ' is-fc' : ''}${i + 1 === closed + 1 ? ' is-fc-edge' : ''}`}>
                {m <= closed ? '' : (planRo ? (disp || '—') : (
                  <input key={`${m}-${raw}`} className="fbs-plan-edit" type="text" inputMode="decimal" placeholder="—"
                    defaultValue={disp} onBlur={(e) => ops.setVal(ln.id, m, e.target.value)} />
                ))}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );

  // Sous-lignes du roulement (dépliées) : + nouvelles ventes, − arrangements
  // (manuel), − encaissement postes ouverts, − encaissement nouvelles ventes.
  const renderRollSubrows = (d, roll) => {
    const cfg = ROLLFORWARD[meta[d.code]?.std_key];
    /* LE DÉTAIL DU PLAN DES VENTES (2026-09-28, la carte Débiteurs fondue ici) :
       sous « Nouvelles ventes », la facturation hors taxes par compte de CA puis
       sa TVA ; sous leur encaissement, l'encaissé hors taxes par compte puis la
       TVA encaissée. Les enfants somment leur parent, chiffres du serveur. */
    const det = (cfg.flow === 'revenue' && view.ar_terms?.plan?.detail) || null;
    const vatPct = det ? `${Math.round(Number(det.vat_rate) * 1000) / 10} %` : '';
    const kids = (k, field, vatSeries, vatLbl, sg) => (
      <>
        {det.lines.map((ln) => (
          <tr key={`${k}-${ln.id || 'none'}`} className="fbs-subrow fbs-subrow--kid">
            <td className="fbs-lbl fbs-sublbl fbs-kidlbl">{ln.id ? ln.label : p('Sans compte')}</td>
            <td className="fbs-num fbs-open" />
            {monthLabels.map((_, i) => numCell(i + 1, sg * (Number(ln[field]?.[i]) || 0)))}
          </tr>
        ))}
        <tr className="fbs-subrow fbs-subrow--kid">
          <td className="fbs-lbl fbs-sublbl fbs-kidlbl">{vatLbl}</td>
          <td className="fbs-num fbs-open" />
          {monthLabels.map((_, i) => numCell(i + 1, sg * (Number(vatSeries?.[i]) || 0)))}
        </tr>
      </>
    );
    // Poste travaillé dans sa propre section → ici, on LIT (pas de saisie).
    const ro = roView || lockedInBilan(d.code);
    const numCell = (m, value, extra = '') => {
      if (m <= closed) return <td key={m} className="fbs-num fbs-subcell" />;
      return <td key={m} className={`fbs-num fbs-subcell is-fc ${extra}`}>{value == null ? '' : fmt(value, unit)}</td>;
    };
    // Bouton + éditeur inline d'un profil d'encaissement (M+1 %, M+2 %).
    /* Le popup est POSITIONNÉ EN FIXE, coordonnées mesurées sur le bouton : dans
     un conteneur à défilement horizontal (`.fbs-wrap { overflow-x: auto }`) un
     popup en position absolue est rogné par le cadre de la card — il devenait
     invisible (constat Olivier 2026-08-17). */
  const openPop = (key, e) => {
    if (editProfile === key) { setEditProfile(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setPopPos({ x: Math.round(r.left), y: Math.round(r.bottom + 4) });
    setEditProfile(key);
  };
  const popStyle = { position: 'fixed', left: popPos.x, top: popPos.y };

  const profileCfg = (kind) => {
      if (ro) return null;      // Σ Bilan : aucun réglage, c'est un résultat
      if (fixedView) return null;   // mono-poste : les tuiles au-dessus de la card règlent (2026-09-12)
      const key = `${d.code}:${kind}`;
      const isOpen = editProfile === key;
      if (kind === 'new') {
        // Connecté aux Ventes : l'encaissement suit les conditions des offres, pas un DSO.
        if (cfg.flow === 'revenue' && view.ar_terms && view.ar_terms.plan) {
          return <span className="fbs-sub-prof"><span className="fbs-sub-cfg" title={p("Encaissé selon les conditions de paiement de chaque offre du plan des ventes")}>{p('selon les offres')}</span></span>;
        }
        const dso = getDso(d.code);
        return (
          <span className="fbs-sub-prof">
            <button type="button" className="fbs-sub-cfg" onClick={(e) => openPop(key, e)} title={`${p('Délai de paiement')} (${cfg.dsoName}, ${p('jours, base 360')})`}>
              {dso} j ⚙
            </button>
            {isOpen && (
              <span className="fbs-prof-pop" style={popStyle}>
                <label>{cfg.dsoName}
                  <input inputMode="numeric" value={dso ?? ''}
                    onChange={(e) => setDso(d.code, e.target.value === '' ? '' : Number(e.target.value.replace(/[^0-9.]/g, '')))} /> j
                </label>
              </span>
            )}
          </span>
        );
      }
      const prof = getProfile(d.code);
      const profLbl = `${prof[0] ?? 0}/${prof[1] ?? 0}${prof[2] ? '/' + prof[2] : ''}`;
      return (
        <span className="fbs-sub-prof">
          <button type="button" className="fbs-sub-cfg" onClick={(e) => openPop(key, e)} title={p("Profil d'encaissement des postes ouverts (M+1, M+2, M+3)")}>
            {profLbl} ⚙
          </button>
          {isOpen && (
            <span className="fbs-prof-pop" style={popStyle}>
              {[0, 1, 2].map((idx) => (
                <label key={idx}>M+{idx + 1}
                  <input inputMode="numeric" value={prof[idx] ?? ''}
                    onChange={(e) => setProfile(d.code, idx, e.target.value === '' ? '' : Number(e.target.value.replace(/[^0-9.]/g, '')))} />%
                </label>
              ))}
            </span>
          )}
        </span>
      );
    };
    return (
      <>
        {arrList(d.code).map((arr) => (
        <tr key={arr.id} className="fbs-subrow fbs-subrow--arr">
          <td className="fbs-lbl fbs-sublbl"><span className="fbs-sub-sign">−</span>
            {ro ? (arr.label || cfg.arrLabel) : (
              <input className="fbs-edit fbs-arrname" value={arr.label || ''}
                placeholder={cfg.arrLabel}
                onChange={(e) => labelArr(d.code, arr.id, e.target.value)}
                onBlur={persistArrangements} />
            )}
            <span className="fbs-sub-manual">{p('manuel')}</span>
            {!ro && (
              <button type="button" className="fbs-arr-x" aria-label={p('Supprimer')}
                onClick={() => { delArr(d.code, arr.id); persistArrangements(); }}>×</button>
            )}
          </td>
          <td className="fbs-num fbs-open" />
          {monthLabels.map((_, i) => {
            const m = i + 1;
            if (m <= closed) return <td key={m} className="fbs-num fbs-subcell" />;
            const cur = arr.vals?.[m];
            // Un arrangement se LIT en négatif, comme une déduction de la créance
            // (2026-09-12) ; il se garde en positif, le moteur le retranche. On
            // tape 50 ou -50, la cellule affiche -50.
            const shown = cur != null && cur !== '' ? `-${toUnit(cur, unit)}` : '';
            return (
              <td key={m} className="fbs-num fbs-subcell is-fc fbs-editcell">
                {ro ? (shown || '—') : (
                <input
                  className="fbs-edit" inputMode="decimal" placeholder="—"
                  value={shown}
                  onChange={(e) => setArr(d.code, arr.id, m, /\d/.test(e.target.value) ? Math.abs(fromUnit(e.target.value, unit)) : '')}
                  onBlur={persistArrangements}
                />
                )}
              </td>
            );
          })}
        </tr>
        ))}
        {!ro && (
        <tr className="fbs-subrow">
          <td className="fbs-lbl fbs-sublbl" colSpan={monthLabels.length + 2}>
            <button type="button" className="fbs-addbtn"
              onClick={() => addArr(d.code)}>＋ {p('Arrangement')}</button>
          </td>
        </tr>
        )}
        <tr className="fbs-subrow">
          <td className="fbs-lbl fbs-sublbl"><span className="fbs-sub-sign">−</span>{cfg.openLabel} {profileCfg('open')}</td>
          <td className="fbs-num fbs-open" />
          {monthLabels.map((_, i) => numCell(i + 1, roll[i + 1] ? -roll[i + 1].encPO : null))}
        </tr>
        <tr className={`fbs-subrow fbs-subrow--add${det ? ' fbs-collapsible' : ''}`}
            onClick={det ? () => setArOpen((o) => ({ ...o, new: !o.new })) : undefined}>
          <td className="fbs-lbl fbs-sublbl">
            {det && <span className="fbs-sub-chev"><Chevron open={arOpen.new} /></span>}
            <span className="fbs-sub-sign">+</span>{cfg.newLabel}
          </td>
          <td className="fbs-num fbs-open" />
          {monthLabels.map((_, i) => numCell(i + 1, roll[i + 1]?.newSales, 'fbs-sub-add'))}
        </tr>
        {det && arOpen.new && kids('new', 'billing', det.vat, `${p('TVA')} ${vatPct}`, 1)}
        <tr className={`fbs-subrow fbs-subrow--newpair${det ? ' fbs-collapsible' : ''}`}
            onClick={det ? () => setArOpen((o) => ({ ...o, enc: !o.enc })) : undefined}>
          <td className="fbs-lbl fbs-sublbl">
            {det && <span className="fbs-sub-chev"><Chevron open={arOpen.enc} /></span>}
            <span className="fbs-sub-sign">−</span>{cfg.dsoLabel} {profileCfg('new')}
          </td>
          <td className="fbs-num fbs-open" />
          {monthLabels.map((_, i) => numCell(i + 1, roll[i + 1] ? -roll[i + 1].encNouv : null))}
        </tr>
        {det && arOpen.enc && kids('enc', 'cash_ht', det.cash_vat, p('TVA encaissée'), -1)}
        {/* La variation du poste, somme des lignes au-dessus (2026-09-28). */}
        {cfg.flow === 'revenue' && (
        <tr className="fbs-subrow fbs-subrow--var">
          <td className="fbs-lbl fbs-sublbl"><span className="fbs-sub-sign">=</span>{p('Variation des créances clients')}</td>
          <td className="fbs-num fbs-open" />
          {monthLabels.map((_, i) => {
            const r = roll[i + 1];
            return numCell(i + 1, r ? r.newSales - r.arrangement - r.encPO - r.encNouv : null);
          })}
        </tr>
        )}
      </>
    );
  };

  /* Sous-lignes de l'onglet Acomptes clients (2026-09-13) : − la reprise de
     l'ouverture en chiffre d'affaires, + les nouveaux acomptes (le plan des
     ventes connecté : facturé d'avance − reconnu ; autonome : encaissés, puis
     repris). Tout vient du serveur (view.advances_view), rien ne se recalcule. */
  const renderAdvSubrows = () => {
    const av = view.advances_view;
    const cell = (m, v, cls = '') => (m <= closed
      ? <td key={m} className="fbs-num fbs-subcell" />
      : <td key={m} className={`fbs-num fbs-subcell is-fc ${cls}`}>{Math.abs(v) > 0.5 ? fmt(v, unit) : '–'}</td>);
    const row = (key, sign, label, series, cls = '') => (
      <tr key={key} className={`fbs-subrow ${cls}`}>
        <td className="fbs-lbl fbs-sublbl"><span className="fbs-sub-sign">{sign}</span>{label}</td>
        <td className="fbs-num fbs-open" />
        {monthLabels.map((_, i) => cell(i + 1, sign === '−' ? -(series[i] || 0) : (series[i] || 0)))}
      </tr>
    );
    return (
      <>
        {row('rel', '−', p('Reprise en chiffre d\'affaires'), av.release)}
        {av.connected
          ? row('plan', '+', p('Facturé d\'avance − reconnu (plan des ventes)'), av.plan, 'fbs-subrow--add')
          : (
            <>
              {row('in', '+', p('Nouveaux acomptes encaissés'), av.new_in, 'fbs-subrow--add')}
              {row('out', '−', p('Reprise des nouveaux acomptes'), av.new_out)}
            </>
          )}
      </>
    );
  };

  // Sections effectivement montrées, après la portée de l'étape.
  const shownSections = sections.filter((sec) => (side === 'all' || sec.code === side)
    && (!fc || fc.readOnly || !fc.sections || fc.sections.includes(sec.code)));

  /* Une étape qui couvre DEUX côtés du bilan (actif circulant et dettes court
     terme) les présente en deux cards distinctes : ce sont deux sujets, pas
     deux blocs d'un même tableau. Hors étape, tout reste dans une seule card. */
  /* Entête standard de carte (2026-09-01) : les contrôles changent la
     REPRÉSENTATION (déployer, filtrer par côté du bilan), ils vivent dans
     l'entête. */
  const renderCard = (secList, key, titleOverride, subOverride, extraControls, close = null) => (
    <CardShell
      key={key}
      className="report-card"
      primaryAction={close}
      title={titleOverride || t('phrases:p_385bd1f4ad')}
      subtitle={subOverride !== undefined ? subOverride : (fixedView
        ? `${p('Départ au dernier réel :')} ${closed >= 1 ? `${t(`common:monthsFull.${closed}`).toLowerCase()} ${year}` : `31.12.${year - 1}`} · ${p('montants TTC en')} ${unit === 'chf' ? 'CHF' : 'kCHF'}`
        : `${p('soldes de fin de mois · départ 31.12.')}${year - 1} · ${unit === 'chf' ? 'CHF' : 'kCHF'}`)}
      viewControls={(
        <div className="fbs-head-actions">
          {extraControls}
          {!fixedView && (
          <button type="button" className="fbs-side-pill" onClick={toggleAllGroups}>
            {allSecCollapsed ? p('Tout déployer') : p('Tout agréger')}
          </button>
          )}
          {(!fc || fc.readOnly) && (
          <div className="fbs-side-pills" role="group" aria-label={p('Filtrer par section du bilan')}>
          {SECTION_PILLS.map(([val, label]) => (
            <button
              key={val}
              type="button"
              className={`fbs-side-pill${side === val ? ' is-active' : ''}`}
              onClick={() => setSide(side === val ? 'all' : val)}
            >{p(label)}</button>
          ))}
          </div>
          )}
        </div>
      )}
    >
      <div className="fbs-wrap">
        <table className="fbs">
          <thead>
            <tr>
              <th className="fbs-lbl" />
              <th className="fbs-num fbs-open">31.12.{String(year - 1).slice(2)}</th>
              {monthLabels.map((m, i) => (
                <th key={m} className={`fbs-num${i + 1 > closed ? ' is-fc' : ''}${i + 1 === closed + 1 ? ' is-fc-edge' : ''}`}>{m}</th>
              ))}
            </tr>
            <tr className="fbs-subhead-af">
              <th className="fbs-lbl" />
              <th className="fbs-num fbs-open" />
              {monthLabels.map((m, i) => (
                <th key={m} className={`fbs-num fbs-af ${i + 1 > closed ? 'fbs-af--f' : 'fbs-af--a'}${i + 1 === closed + 1 ? ' is-fc-edge' : ''}`}>
                  {i + 1 > closed ? p('prév.') : p('réel')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {secList.map((sec) => {
              const st = rows.find((r) => r.type === 'section_total' && r.section === sec.code);
              const details = rows.filter((r) => r.type === 'detail' && r.section === sec.code && inFocus(r));
              const isCollapsed = fixedView ? false : collapsed.has(sec.code);
              return (
                <React.Fragment key={sec.code}>
                  {!fixedView && (
                  <tr className="fbs-section fbs-collapsible" onClick={() => toggle(sec.code)}>
                    <td className="fbs-lbl">
                      <span className="fbs-caret-slot" aria-label={isCollapsed ? p('Déplier') : p('Replier')}><Chevron open={!isCollapsed} /></span>
                      {sec.label}
                    </td>
                    {openCell(st)}
                    {readMonths(st)}
                  </tr>
                  )}
                  {!isCollapsed && details.map((d) => {
                    // Une cellule ne s'édite plus QUE dans une étape (section
                    // dédiée) : le bilan complet est en lecture seule (2026-08-20).
                    const editable = !isDerived(d.code) && !roView && !!fc && !lockedInBilan(d.code);
                    const isRoll = !!ROLLFORWARD[meta[d.code]?.std_key] && !!rollByLine[d.code];
                    // Ligne à rôle (TVA / charges sociales) : le solde est
                    // dicté par le plan de liquidité — pas de saisie manuelle.
                    const roleDriven = ['vat', 'social'].includes(meta[d.code]?.line_role);
                    const ovLine = override[d.code];
                    const isExpanded = fixedView || expandedRoll.has(d.code);
                    /* Mois prévisionnels SAISIS de la ligne — d'après la BASE
                       (manual_months), pas l'affichage : une saisie écrasée par
                       un calcul (ligne à rôle, acomptes cards) ou un 0 saisi
                       restent invisibles à l'écran. Le bouton × les révèle et
                       les purge (demande Olivier 2026-08-20). */
                    const manualMonths = (d.manual_months || []).map(Number);
                    // Variations des mois prévisionnels (bilan complet) : Δ vs
                    // mois précédent — la valeur qui bouge se met en évidence,
                    // l'infobulle au survol dit la variation et son origine.
                    const valAt = (m) => {
                      if (m < 1) return Number(d.prev_dec) || 0;
                      const v = (ovLine && ovLine[m] != null) ? ovLine[m] : (d.months ? d.months[String(m)] : null);
                      return Number(v) || 0;
                    };
                    return (
                      <React.Fragment key={d.code}>
                      <tr className={`fbs-detail${editable ? ' is-editable' : ''}${isRoll ? ' fbs-detail--roll fbs-collapsible' : ''}`}>
                        <td className={`fbs-lbl${isRoll ? ' fbs-lbl--cfg' : ''}`}>
                          <span className="fbs-caret-slot" onClick={(isRoll && !fixedView) ? () => toggleRoll(d.code) : undefined}>
                            {isRoll && !fixedView && <Chevron open={isExpanded} />}
                          </span>
                          <span
                            className="fbs-lbl-txt"
                            onClick={(isRoll && !fixedView) ? () => toggleRoll(d.code) : undefined}
                            title={(isRoll && !fixedView) ? p('Déplier / replier') : undefined}
                          >
                            {d.label}
                          </span>
                          {/* Purge permise même sur une ligne verrouillée (rôle,
                              cards) : c'est là que les saisies mortes traînent —
                              le serveur n'autorise de toute façon que l'effacement. */}
                          {!roView && manualMonths.length > 0 && (
                            <button
                              type="button"
                              className="fbs-purge"
                              title={`${p('Effacer les')} ${manualMonths.length} ${p('saisies manuelles (retour au calcul)')}`}
                              onClick={(e) => { e.stopPropagation(); onPurgeManual(d.code, manualMonths); }}
                            >×{manualMonths.length}</button>
                          )}
                        </td>
                        {openCell(d)}
                        {monthLabels.map((_, i) => {
                          const m = i + 1;
                          const isFc = m > closed;
                          const val = (ovLine && ovLine[m] != null) ? ovLine[m] : (d.months ? d.months[String(m)] : null);
                          const ini = m === closed ? initFor(d.code) : null;
                          if (ini) return initTd(ini, i);
                          return (
                            <MonthCell
                              key={i}
                              value={val}
                              unit={unit}
                              isFc={isFc}
                              first={m === closed + 1}
                              varMark={fullView && isFc && Math.abs(valAt(m) - valAt(m - 1)) > 0.5}
                              varTip={(fullView && isFc && Math.abs(valAt(m) - valAt(m - 1)) > 0.5) ? (
                                <>
                                  <b>{(valAt(m) - valAt(m - 1) > 0 ? '+' : '') + fmt(valAt(m) - valAt(m - 1), unit)}</b>
                                  <span>{varOriginLabel(d.fc_src?.[String(m)], p, isRoll)}</span>
                                </>
                              ) : null}
                              editable={editable && !isRoll && !roleDriven}
                              onCommit={(amt) => saveCell(d.code, i, amt)}
                              title={roleDriven && isFc
                                ? (meta[d.code]?.line_role === 'vat' ? p('Piloté par le décompte TVA (plan de liquidité)') : p('Piloté par l\'échéancier des charges sociales'))
                                : (isRoll && isFc ? p('Solde de roulement (déplier pour le détail)') : (isFc ? auditText(d.fc_src?.[String(m)], val, unit, p) : p('Réel (mois clôturé)')))}
                            />
                          );
                        })}
                      </tr>
                      {isRoll && isExpanded && renderRollSubrows(d, rollByLine[d.code])}
                      {fixedView && meta[d.code]?.std_key === 'customer_advances' && view.advances_view && renderAdvSubrows()}
                      </React.Fragment>
                    );
                  })}
                  {!isCollapsed && sec.code === 'fixed_assets' && planShown('cessions') && renderPlanRows(cessionLines, planOps('bs_cessions', cessionLines), 'cessions')}
                  {!isCollapsed && sec.code === 'fixed_assets' && planShown('capex') && renderPlanRows(capexLines, planOps('bs_capex', capexLines), 'capex')}
                  {!isCollapsed && sec.code === 'long_term_liabilities' && planShown('financing') && renderPlanRows(finLines, planOps('bs_financing', finLines), 'financing')}
                </React.Fragment>
              );
            })}
            {subtotals.filter((s) => {
              // AUCUN sous-total dans une étape : « Total Actifs », « Total
              // Passifs » et le contrôle d'équilibre portent sur le bilan
              // ENTIER, alors que l'étape n'en montre qu'une poignée de lignes.
              // Le chiffre ne se rattacherait à rien de visible. Ils se lisent
              // à l'étape Σ Bilan, seule vue complète.
              if (fc && !fc.readOnly) return false;
              return side === 'all' || SUBTOTAL_SIDE[s.code] === sectionSide(side);
            }).map((s) => (
              <tr key={s.code} className="fbs-subtotal">
                <td className="fbs-lbl">{s.label}</td>
                {openCell(s)}
                {readMonths(s)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardShell>
  );

  // Étape à plusieurs sections → une card par section, dans l'ordre du bilan.
  if (fc && !fc.readOnly && shownSections.length > 1) {
    return <>{shownSections.map((sec, i) => renderCard([sec], sec.code, sec.label, undefined, undefined, i === 0 ? closeAction : null))}</>;
  }
  /* POSTE SEUL (Créances-clients, Dettes fournisseurs ; proto du 2026-09-12) :
     la card porte le nom du poste, et DEUX tuiles au-dessus disent les deux
     sources du solde et portent leurs hypothèses. Source 01, la comptabilité :
     le solde ouvert au dernier réel, et le profil qui l'encaisse (M+1 / M+2 /
     M+3, hors arrangements). Source 02, les flux nouveaux : le plan des ventes
     connecté (les délais des offres, rien à régler) ou le compte de résultat
     avec la TVA (le DSO se règle ici). Les ⚙ des sous-lignes se taisent. */
  if (fixedView) {
    const fl = (view?.rows || []).find((r) => r.type === 'detail' && inFocus(r) && rollByLine[r.code]);
    if (fl) {
      const cfg = ROLLFORWARD[meta[fl.code]?.std_key] || {};
      const isAr = cfg.flow === 'revenue';
      const planDriven = isAr && !!(view?.ar_terms && view.ar_terms.plan);
      const prof = getProfile(fl.code);
      const mFull = (m) => t(`common:monthsFull.${m}`);
      const stepLbl = (i) => (closed + 1 + i <= 12 ? `${mFull(closed + 1 + i)} · M+${i + 1}` : `M+${i + 1}`);
      const numIn = (val, onChange, extra = '') => (
        <input className={`fbs-src-in${extra}`} inputMode="numeric" value={val ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value.replace(/[^0-9.]/g, '')) || 0)} />
      );
      /* LES RÉGLAGES MONTENT DANS L'ENTÊTE (demande d'Olivier, 2026-09-24) :
         le profil d'encaissement des postes ouverts et le délai des nouvelles
         factures changent la BASE DE CALCUL de la table, leur place est donc
         l'entête de sa carte. Ils vivaient dans deux cartes « Source » posées
         au-dessus, qui disaient surtout d'où venaient les chiffres : la
         connexion au module Ventes l'annonce désormais sur la rangée des
         étapes, une fois pour toute la section. Le solde d'ouverture, lui, est
         un CONTEXTE : il passe en sous-titre. */
      const reglages = (
        <div className="fbs-head-set">
          <span className="fbs-head-set__lbl">{isAr ? p('Encaissement hors arrangements') : p('Paiement hors arrangements')}</span>
          {[0, 1, 2].map((i) => (
            <label key={i} className="fbs-head-set__f">
              <span>{stepLbl(i)}</span>
              <span className="fbs-src__in">{numIn(pFmt(prof[i]), (v) => setProfile(fl.code, i, v === '' ? 0 : v))}<span className="fbs-src__unit">%</span></span>
            </label>
          ))}
          {!planDriven && (
            <label className="fbs-head-set__f">
              {/* LE SIGLE RESTE AU POPUP TECHNIQUE (2026-09-25) : « DSO » dit ce
                  qu'on mesure à qui connaît le sigle ; ici on nomme ce qu'on
                  RÈGLE, le délai des factures à venir. */}
              <span>{isAr ? p('Délai nouvelles factures') : p('Délai nouveaux achats')}</span>
              <span className="fbs-src__in">{numIn(getDso(fl.code), (v) => setDso(fl.code, v === '' ? 0 : v), ' fbs-src-in--dso')}<span className="fbs-src__unit">j</span></span>
            </label>
          )}
        </div>
      );
      /* SOUS LE TITRE, LES VARIABLES ET RIEN D'AUTRE (2026-09-25). Elles
         tenaient d'abord la ligne du titre, où elles poussaient le nom de la
         carte ; elles ont ensuite voisiné un rappel du solde d'ouverture, que
         la table affiche déjà dans sa première colonne. Un texte qui décrit
         l'écran n'aide personne et repousse la donnée d'un rang. */
      return renderCard(shownSections, 'all', fl.label, reglages, undefined, closeAction);
    }
  }
  /* ONGLET ACOMPTES CLIENTS (2026-09-13, cartes de source retirées le
     2026-09-24) : pas de roulement, un déroulé. Le solde au dernier réel se
     reprend en chiffre d'affaires sur n mois, et ce réglage est LE seul de
     l'écran : il se pose donc sous le titre de la carte, là où on le lit en
     même temps que la table qu'il commande. Les deux cartes « Source » qui le
     portaient disaient surtout d'où venaient les chiffres, ce que la connexion
     au module Ventes annonce désormais sur la rangée des étapes. */
  if (fixedView) {
    const al = (view?.rows || []).find((r) => r.type === 'detail' && inFocus(r) && meta[r.code]?.std_key === 'customer_advances');
    if (al) {
      const months = assum?.advances_release?.months;
      const reprise = (
        <span className="fbs-sub-set">
          {p('Reprise en chiffre d\'affaires')} : {p('linéaire, sur')}
          <input className="fbs-src-in fbs-src-in--dso" inputMode="numeric" value={months ?? ''} placeholder="–"
            aria-label={p('Reprise en chiffre d\'affaires')}
            onChange={(e) => {
              const n = Number(e.target.value.replace(/[^0-9]/g, '')) || 0;
              saveRatio('advances_release', n > 0 ? { months: n } : null);
            }} />
          {p('mois')}
        </span>
      );
      return renderCard(shownSections, 'all', al.label, reprise, undefined, closeAction);
    }
  }
  return renderCard(shownSections, 'all', undefined, undefined, undefined, closeAction);
}
